import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { showErrorToast } from "src/utils/methods";
import { useDispatch } from "react-redux";
import axios from "axios";

import { identify } from "src/redux/main/mainActions";
import { API_URLS } from "../../utils/API_URLS";
import SubscriptionSuccessBox, {
  SubscriptionSuccessBoxLoader,
  SubscriptionSuccessBoxText,
} from "../subscription/components/SubscriptionSuccessBox/SubscriptionSuccessBox";
import IDHFormattedMessage from "../components/IDHFormattedMessage/IDHFormattedMessage";

function ConfirmRegistrationPage() {
  const { search } = useLocation();

  const [loading, setLoading] = useState<boolean>(true);
  const [isPaidSubscriptionPlan, setIsPaidSubscriptionPlan] =
    useState<boolean>(false);
  const [creatingWorkspaceInProgress, setCreatingWorkspaceInProgress] =
    useState<boolean>(false);
  const [workspaceUuidReady, setWorkspaceUuidReady] = useState<string | null>(
    null,
  );

  const isWorkspaceReadyRef = useRef<ReturnType<typeof setInterval> | null>();
  const history = useHistory();
  const dispatch = useDispatch();

  const getEmailConfirmedData = async () => {
    const params = new URLSearchParams(search);
    const workspaceData = params.get("workspaceData") ?? null;

    try {
      setLoading(true);
      const data = {
        workspaceData,
      };
      await axios.post(API_URLS.registerAndConfirmEmail, data).then((res) => {
        if (res.data.content.isPaidSubscriptionPlan) {
          setIsPaidSubscriptionPlan(true);
          setCreatingWorkspaceInProgress(true);
        } else {
          setIsPaidSubscriptionPlan(false);
        }
      });
      setLoading(false);
    } catch (err) {
      showErrorToast();
    }
  };

  useEffect(() => {
    getEmailConfirmedData();
  }, []);

  const isWorkspaceReady = () => {
    try {
      const params = new URLSearchParams(search);
      const workspaceData = params.get("workspaceData") ?? null;

      const data = {
        workspaceData,
      };
      axios.post(API_URLS.isWorkspaceReady, data).then((res) => {
        if (res.data.content.ready) {
          setWorkspaceUuidReady(res.data.content.magicLink);
        }
      });
    } catch (err) {
      showErrorToast();
    }
  };

  useEffect(() => {
    if (isPaidSubscriptionPlan) {
      isWorkspaceReadyRef.current = setInterval(isWorkspaceReady, 5000);
    }
  }, [isPaidSubscriptionPlan]);

  useEffect(() => {
    if (workspaceUuidReady && isWorkspaceReadyRef.current) {
      clearInterval(isWorkspaceReadyRef.current);
    }
  }, [workspaceUuidReady]);

  const renderBoxContent = () => {
    if (workspaceUuidReady) {
      return (
        <SubscriptionSuccessBoxText bold>
          <IDHFormattedMessage
            id="ws_you_are_one_step_away_from_starting_with_indahash_complete_the_payment_to_get_started"
            defaultMessage="You’re one step away from starting with indaHash. Complete the payment to get started."
          />
        </SubscriptionSuccessBoxText>
      );
    }

    if (isPaidSubscriptionPlan && creatingWorkspaceInProgress) {
      return (
        <SubscriptionSuccessBoxLoader>
          <IDHFormattedMessage
            id="ws_please_wait_we_are_preparing_your_workspace"
            defaultMessage="Please wait... We are preparing your workspace."
          />
        </SubscriptionSuccessBoxLoader>
      );
    }

    return (
      <SubscriptionSuccessBoxText bold marginTop={16}>
        <IDHFormattedMessage
          id="ws_please_be_patient_you_will_receive_an_activation_email_confirmation"
          defaultMessage="Please be patient. You’ll receive an activation email confirmation (within up to 2 hours), after which you can start using the platform."
        />
      </SubscriptionSuccessBoxText>
    );
  };

  const handleContinueClick = async (wsWorkspaceUuid: string) => {
    if (!workspaceUuidReady) return;
    try {
      await axios.get(workspaceUuidReady);
    } finally {
      history.push("/workspace/");
      dispatch(identify(wsWorkspaceUuid));
    }
  };

  if (loading) {
    return null;
  }

  const url = workspaceUuidReady ? new URL(workspaceUuidReady) : null;
  const wsWorkspaceUuid = url?.searchParams?.get("wsWorkspaceUuid");

  return (
    <SubscriptionSuccessBox
      onContinueButtonClick={
        wsWorkspaceUuid ? () => handleContinueClick(wsWorkspaceUuid) : undefined
      }
      backButtonFunction={() => {
        window.location.href = window.location.origin;
      }}
      backButtonText={
        <IDHFormattedMessage
          id="ws_back_to_homepage"
          defaultMessage="Back to homepage"
        />
      }
    >
      <SubscriptionSuccessBoxText>
        <IDHFormattedMessage
          id="ws_your_email_address_has_been_successfully_confirmed"
          defaultMessage="Your email address has been successfully confirmed."
        />
      </SubscriptionSuccessBoxText>

      {renderBoxContent()}
    </SubscriptionSuccessBox>
  );
}

export { ConfirmRegistrationPage };
